import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  login as apiLogin,
  register as apiRegister,
  logout as apiLogout,
  refreshToken as apiRefreshToken,
} from "@/api/auth";

export const useAuthStore = defineStore("auth", () => {
  const user = ref(JSON.parse(localStorage.getItem("user")));
  const token = ref(localStorage.getItem("token"));
  const refreshToken = ref(localStorage.getItem("refreshToken"));
  const tokenExpiration = ref(localStorage.getItem("tokenExpiration"));
  const returnUrl = ref(null);

  const isAuthenticated = computed(() => !!token.value);

  // 检查令牌是否快要过期
  const isTokenExpiringSoon = computed(() => {
    if (!tokenExpiration.value) return false;
    const expiryDate = new Date(tokenExpiration.value);
    const now = new Date();
    // 如果令牌将在5分钟内过期，视为即将过期
    return expiryDate.getTime() - now.getTime() < 5 * 60 * 1000;
  });

  async function login(email, password) {
    try {
      const response = await apiLogin(email, password);
      console.log(response);
      if (response.success && response.token && response.user) {
        user.value = response.user;
        token.value = response.token;
        refreshToken.value = response.refreshToken;
        tokenExpiration.value = response.expiration;

        // Store user details and tokens in localStorage
        localStorage.setItem("user", JSON.stringify(user.value));
        // Note: Token is already saved in localStorage by the API function

        return { success: true };
      } else {
        return { success: false, message: response.message || "登录失败" };
      }
    } catch (error) {
      return { success: false, message: error.message };
    }
  }

  async function register(userData) {
    try {
      // 确保包含确认密码
      const dataToSend = {
        ...userData,
        confirmPassword: userData.confirmPassword || userData.password,
      };
      
      const response = await apiRegister(dataToSend);
      
      // 如果注册成功，自动登录
      if (response.success && response.token) {
        user.value = response.user;
        token.value = response.token;
        refreshToken.value = response.refreshToken || '';
        tokenExpiration.value = response.expiration || '';
        
        // 存储用户信息和令牌
        localStorage.setItem('user', JSON.stringify(user.value));
        localStorage.setItem('token', token.value);
        localStorage.setItem('refreshToken', refreshToken.value);
        localStorage.setItem('tokenExpiration', tokenExpiration.value);
      }
      
      return response;
    } catch (error) {
      console.error('注册失败:', error);
      return { 
        success: false, 
        message: error.message || '注册失败，请稍后重试' 
      };
    }
  }

  async function tryRefreshToken() {
    if (token.value && refreshToken.value) {
      try {
        const response = await apiRefreshToken(token.value, refreshToken.value);
        if (response.success && response.token) {
          token.value = response.token;
          refreshToken.value = response.refreshToken;
          tokenExpiration.value = response.expiration;
          return true;
        }
      } catch (error) {
        console.error("Failed to refresh token:", error);
        // If refresh fails, clear auth state
        logout();
      }
    }
    return false;
  }

  async function logout() {
    try {
      await apiLogout();
    } finally {
      // Clear user data from store and localStorage
      user.value = null;
      token.value = null;
      refreshToken.value = null;
      tokenExpiration.value = null;
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("tokenExpiration");
    }
  }

  return {
    user,
    token,
    refreshToken,
    tokenExpiration,
    returnUrl,
    isAuthenticated,
    isTokenExpiringSoon,
    login,
    register,
    tryRefreshToken,
    logout,
  };
});
